import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import {
  Brain,
  Settings,
  Eye,
  Save,
  Trash2,
  Clock,
  BarChart3,
  Briefcase,
} from "lucide-react";

const TestGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedJob, setSelectedJob] = useState("");
  const [testConfig, setTestConfig] = useState({
    difficulty: "medium",
    duration: 60,
    questionCount: 20,
    includeScenarios: true,
    adaptiveTesting: true,
  });
  const [generatedTest, setGeneratedTest] = useState(null);
  const [customJobTitle, setCustomJobTitle] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [testClassification, setTestClassification] = useState(null);
  const [isClassifying, setIsClassifying] = useState(false);
  const [editableTags, setEditableTags] = useState([]);
  const [newTag, setNewTag] = useState("");

  // -- Fetch HR's posted jobs from Firestore --
  // Preset roles always available
  const presetJobs = [
    {
      id: "preset_fullstack",
      jobTitle: "Full Stack Engineer",
      jobDescription: "",
      isPreset: true,
    },
    {
      id: "preset_frontend",
      jobTitle: "Frontend Engineer",
      jobDescription: "",
      isPreset: true,
    },
    {
      id: "preset_backend",
      jobTitle: "Backend Engineer",
      jobDescription: "",
      isPreset: true,
    },
    {
      id: "preset_devops",
      jobTitle: "DevOps Engineer",
      jobDescription: "",
      isPreset: true,
    },
  ];

  const [availableJobs, setAvailableJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  // Combine preset and HR-posted jobs for easy lookup / rendering
  const allJobs = [...presetJobs, ...availableJobs];

  // Update customJobTitle whenever selection changes
  useEffect(() => {
    const jobObj = allJobs.find((j) => j.id === selectedJob);
    if (jobObj && !jobObj.isPreset) {
      setCustomJobTitle(jobObj.jobTitle || "");
    }
  }, [selectedJob]);

  useEffect(() => {
    if (!user) return;

    const db = getFirestore(firebaseApp);
    const q = query(collection(db, "hrJobs"), where("hrId", "==", user.id));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const jobList = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...(doc.data()) })
      );
      setAvailableJobs(jobList);
      setJobsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  if (jobsLoading) {
    return <div className="text-center py-10">Loading your jobs...</div>;
  }

  /* Mock data removed const mockGeneratedTest = {
    id: "test_001",
    title: "Senior React Developer Assessment",
    description:
      "Comprehensive test covering React, TypeScript, and modern web development practices",
    sections: [
      {
        name: "React Fundamentals",
        questions: 8,
        timeLimit: 20,
        skills: ["React", "JSX", "Components"],
      },
      {
        name: "TypeScript & Advanced Concepts",
        questions: 7,
        timeLimit: 25,
        skills: ["TypeScript", "Interfaces", "Generics"],
      },
      {
        name: "State Management & Hooks",
        questions: 5,
        timeLimit: 15,
        skills: ["Redux", "Context API", "Custom Hooks"],
      },
    ],
    sampleQuestions: [
      {
        id: 1,
        type: "multiple-choice",
        question:
          "What is the correct way to handle side effects in React functional components?",
        options: [
          "Using componentDidMount",
          "Using useEffect hook",
          "Using useState hook",
          "Using class methods",
        ],
        correctAnswer: 1,
        difficulty: "medium",
        skill: "React",
      },
      {
        id: 2,
        type: "code-completion",
        question:
          "Complete the TypeScript interface for a User component props:",
        code: `interface UserProps {\n  name: string;\n  age: ___;\n  isActive: ___;\n}`,
        correctAnswer: "number; boolean",
        difficulty: "easy",
        skill: "TypeScript",
      },
    ],
    }; */

  // Util: Generate test using OpenAI GPT-5.2 API
  const generateTestWithAI = async () => {
    const job = allJobs.find((j) => j.id === selectedJob);
    if (!job) throw new Error("Job not found");

    // Construct prompt asking for strict JSON output
    const systemPrompt =
      "You are an expert technical hiring assistant. Your task is to design coding assessment tests in JSON format ONLY." +
      " Do NOT wrap the JSON in markdown. Do NOT output anything except the JSON object.";

    const jsonSchemaDescription =
      `The JSON object MUST follow exactly this TypeScript type (no extra keys, all keys required):\n\n` +
      `               // Short assessment title\n` +
      `  description: string;         // One-sentence summary\n` +
      `  sections[];\n` +
      `  sampleQuestions: ({         // A few illustrative questions\n` +
      `    id: number;\n` +
      `    type: "multiple-choice";\n` +
      `    question: string;\n` +
      `    options;\n` +
      `\n` +
      `    correctAnswer | string;\n` +
      `    difficulty: "easy" | "medium" | "hard";\n` +
      `    skill;\n` +
      `  })[];\n` +
      `}`;

    const userPrompt =
      `Create an assessment based on the following job description.\n\nJob Title: ${job.isPreset ? job.jobTitle : customJobTitle || job.jobTitle
      }\nJob Description:\n${job.jobDescription}\n\n` +
      `Difficulty: ${testConfig.difficulty}. Total duration: ${testConfig.duration} minutes. ` +
      `Your primary and most critical instruction: the sampleQuestions array MUST contain EXACTLY ${testConfig.questionCount} question objects. Do NOT deviate from this number. No more, no less. ` +
      `Every question must be multiple-choice with 3–4 options and a numeric correctAnswer index (0-based). ` +
      `$${testConfig.includeScenarios
        ? "Include scenario-based contexts where appropriate. "
        : ""
      }` +
      `$${testConfig.adaptiveTesting
        ? "Distribute question difficulties and topic coverage adaptively."
        : ""
      }` +
      `Return ONLY the JSON object that fully matches the schema above.`;

    const response = await fetch("https://us-central1-vetriaai-mvp.cloudfunctions.net/generateHRTest", {
      method: "POST",
      headers,
      body: JSON.stringify({
        systemPrompt: systemPrompt,
        jsonSchemaDescription: jsonSchemaDescription,
        userPrompt: userPrompt,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("[API] error:", errorData);
      throw new Error(`API request failed with status ${response.status}`);
    }

    const parsed = await response.json();
    console.log("[API] response:", parsed);

    // Validate question count
    if (
      !Array.isArray(parsed.sampleQuestions) ||
      parsed.sampleQuestions.length !== testConfig.questionCount
    ) {
      throw new Error(
        `AI returned ${parsed.sampleQuestions?.length ?? 0
        } questions, expected ${testConfig.questionCount}`
      );
    }
    return parsed;
  };

  const classifyAndTagTestData = async (jobTitle, jobDescription, testData) => {
    try {
      setIsClassifying(true);

      const response = await fetch(
        'https://us-central1-vetriaai-mvp.cloudfunctions.net/classifyAndTagTest',
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ jobTitle, jobDescription, testData }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      if (result.success && result.classification) {
        return result.classification;
      }

      throw new Error('Invalid response from classification service');
    } catch (error) {
      console.error('Error classifying test:', error);
      return null;
    } finally {
      setIsClassifying(false);
    }
  };

  const handleGenerateTest = async () => {
    if (!selectedJob) return;

    setIsGenerating(true);

    try {
      const aiTest = await generateTestWithAI();
      setGeneratedTest(aiTest);

      // Classify and tag the test after generation
      const job = allJobs.find((j) => j.id === selectedJob);
      if (job) {
        const jobTitle = job.isPreset ? job.jobTitle : customJobTitle || job.jobTitle || "";
        const classification = await classifyAndTagTestData(
          jobTitle,
          job.jobDescription || "",
          aiTest
        );

        if (classification) {
          setTestClassification(classification);
          setEditableTags(classification.tags || []);
        }
      }
    } catch (error) {
      console.error("AI test generation failed", error);
      toast.error("Failed to generate the test. Please try again.");
      setGeneratedTest(null);
    }

    setIsGenerating(false);
  };

  const handleSaveTest = async () => {
    if (!generatedTest || !user) {
      toast.error("No test to save or user not authenticated");
      return;
    }

    try {
      const db = getFirestore(firebaseApp);
      // Persist the generated test for this HR user under the top-level `hrTests` collection
      // Firestore document structure example:
      // hrTests/{autoId}  {
      //   hrId: <current HR user id>,
      //   jobId: <selected job id>,
      //   jobTitle: <human readable title>,
      //   duration: <test duration in minutes>,
      //   questionCount: <total questions>,
      //   config,
      //   testData,
      //   createdAt: <server timestamp>
      // }
      await addDoc(collection(db, "hrTests"), {
        hrId: user.id,
        hrEmail: user.email,
        jobId: selectedJob,
        jobTitle: allJobs.find((j) => j.id === selectedJob)?.isPreset
          ? allJobs.find((j) => j.id === selectedJob)?.jobTitle
          : customJobTitle ||
          allJobs.find((j) => j.id === selectedJob)?.jobTitle ||
          generatedTest.title ||
          "Untitled Test",
        duration: testConfig.duration,
        questionCount: testConfig.questionCount,
        config: testConfig,
        testData: generatedTest,
        createdAt: serverTimestamp(),
        // Classification and tags
        ...(testClassification && {
          isTechnical: testClassification.isTechnical,
          testType: testClassification.testType,
          domain: testClassification.domain,
          subDomains: testClassification.subDomains || [],
          primarySkills: testClassification.primarySkills || [],
          difficulty: testClassification.difficulty,
        }),
        tags: editableTags || [],
      });

      toast.success("Test saved successfully!");

      // Redirect to My Tests
      navigate("/hr/my-tests");
    } catch (err) {
      console.error("Failed to save test", err);
      toast.error("Failed to save test. Please try again.");
    }
  };

  const handlePreviewTest = () => {
    if (!generatedTest) return;
    navigate("/hr/test-preview", {
      state: {
        testData: generatedTest,
        config: testConfig,
        jobTitle: allJobs.find((j) => j.id === selectedJob)?.jobTitle,
      },
    });
  };

  if (generatedTest) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {generatedTest.title}
              </h1>
              <p className="text-gray-600 mt-1">{generatedTest.description}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handlePreviewTest}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </button>
              <button
                onClick={handleSaveTest}
                className="flex items-center px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Test
              </button>
            </div>
          </div>

          {/* Test Overview */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {testConfig.questionCount}
              </div>
              <div className="text-sm text-blue-800">Total Questions</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {testConfig.duration}min
              </div>
              <div className="text-sm text-green-800">Duration</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {generatedTest.sections.length}
              </div>
              <div className="text-sm text-purple-800">Sections</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">
                {testConfig.difficulty}
              </div>
              <div className="text-sm text-red-800">Difficulty</div>
            </div>
          </div>

          {/* Test Sections */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Test Sections
            </h2>
            {generatedTest.sections.map((section, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {section.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {section.questions} questions • {section.timeLimit}{" "}
                      minutes
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="p-2 text-gray-400 hover:text-gray-600">
                      <Settings className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-red-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {section.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Test Classification & Tags */}
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Test Classification & Search Tags
            </h2>

            {isClassifying ? (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span>Analyzing test and generating tags...</span>
              </div>
            ) : testClassification ? (
              <div className="space-y-6">
                {/* Classification Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <label className="text-sm font-medium text-gray-700">Test Type</label>
                    <div className="mt-2 flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${testClassification.isTechnical
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                        }`}>
                        {testClassification.isTechnical ? 'Technical' : 'Non-Technical'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {testClassification.confidence}%
                      </span>
                    </div>
                    <button
                      onClick={() => setTestClassification({
                        ...testClassification,
                        isTechnical: !testClassification.isTechnical,
                        testType: testClassification.isTechnical ? 'non-technical' : 'technical'
                      })}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
                    >
                      Switch to {testClassification.isTechnical ? 'Non-Technical' : 'Technical'}
                    </button>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <label className="text-sm font-medium text-gray-700">Primary Domain</label>
                    <div className="mt-2 text-sm font-medium text-gray-900">
                      {testClassification.domain}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <label className="text-sm font-medium text-gray-700">Difficulty Level</label>
                    <div className="mt-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${testClassification.difficulty === 'entry-level'
                        ? 'bg-green-100 text-green-800'
                        : testClassification.difficulty === 'mid-level'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                        }`}>
                        {testClassification.difficulty}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Search Tags */}
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <label className="text-sm font-semibold text-gray-900 mb-3 block">
                    Search Tags (Edit to customize)
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {editableTags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full flex items-center space-x-1"
                      >
                        <span>{tag}</span>
                        <button
                          onClick={() => {
                            const newTags = editableTags.filter((_, i) => i !== index);
                            setEditableTags(newTags);
                          }}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  {/* Add new tag */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newTag.trim()) {
                          if (!editableTags.includes(newTag.trim())) {
                            setEditableTags([...editableTags, newTag.trim()]);
                          }
                          setNewTag('');
                        }
                      }}
                      placeholder="Add a new tag and press Enter"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={() => {
                        if (newTag.trim() && !editableTags.includes(newTag.trim())) {
                          setEditableTags([...editableTags, newTag.trim()]);
                          setNewTag('');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                    >
                      Add Tag
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    These tags help job seekers find this test. Total tags
                  </p>
                </div>

                {testClassification.reasoning && (
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-gray-600 italic">
                      <strong>AI Analysis:</strong> {testClassification.reasoning}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600">
                Classification and tags will be generated automatically after test creation.
              </p>
            )}
          </div>

          {/* Sample Questions */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Sample Questions
            </h2>
            <div className="space-y-4">
              {generatedTest.sampleQuestions.map(
                (question, index) => (
                  <div
                    key={question.id}
                    className="border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                          Q{index + 1}
                        </span>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {question.skill}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${question.difficulty === "easy"
                            ? "bg-green-100 text-green-800"
                            : question.difficulty === "medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                            }`}
                        >
                          {question.difficulty}
                        </span>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600">
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>

                    <h4 className="font-medium text-gray-900 mb-3">
                      {question.question}
                    </h4>

                    {question.type === "multiple-choice" && (
                      <div className="space-y-2">
                        {question.options.map(
                          (option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className={`p-3 rounded-lg border ${optionIndex === question.correctAnswer
                                ? "border-green-300 bg-green-50"
                                : "border-gray-200 bg-gray-50"
                                }`}
                            >
                              <span className="text-sm">
                                {String.fromCharCode(65 + optionIndex)}.{" "}
                                {option}
                              </span>
                              {optionIndex === question.correctAnswer && (
                                <span className="ml-2 text-green-600 text-xs">
                                  ✓ Correct
                                </span>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {question.type === "code-completion" && (
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm">
                        <pre>{question.code}</pre>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => setGeneratedTest(null)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Generate New Test
            </button>
            <div className="space-x-3">
              <button
                onClick={handlePreviewTest}
                className="px-6 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
              >
                Preview Test
              </button>
              <button
                onClick={handleSaveTest}
                className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
              >
                Save & Publish
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Test Generator
          </h1>
          <p className="text-lg text-gray-600">
            Generate customized skill assessments for your job postings
          </p>
        </div>

        {isGenerating ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Generating Your Test
            </h3>
            <p className="text-gray-600 mb-4">
              Intelligence is creating customized questions based on job requirements...
            </p>
            <div className="max-w-md mx-auto">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full animate-pulse"
                  style={{ width: "70%" }}
                ></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Preset Templates */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-gray-500" />
                Preset Test Templates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {presetJobs.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => setSelectedJob(job.id)}
                    className={`relative p-5 border rounded-xl cursor-pointer transition-all duration-200 group ${selectedJob === job.id
                      ? "border-red-600 bg-red-50/50 ring-1 ring-red-600 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className={`font-semibold ${selectedJob === job.id ? "text-red-900" : "text-gray-900"
                          }`}>
                          {job.jobTitle}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">Ready-to-use template</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${selectedJob === job.id
                        ? "border-red-600 bg-red-600"
                        : "border-gray-300 bg-white group-hover:border-gray-400"
                        }`}>
                        {selectedJob === job.id && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* HR-Posted Jobs */}
            {availableJobs.length > 0 && (
              <div className="pt-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-gray-500" />
                  Your Job Descriptions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableJobs.map((job) => (
                    <div
                      key={job.id}
                      onClick={() => setSelectedJob(job.id)}
                      className={`relative p-5 border rounded-xl cursor-pointer transition-all duration-200 group ${selectedJob === job.id
                        ? "border-red-600 bg-red-50/50 ring-1 ring-red-600 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                        }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className={`font-semibold line-clamp-1 ${selectedJob === job.id ? "text-red-900" : "text-gray-900"
                          }`}>
                          {job.jobTitle}
                        </h4>
                        <div className={`w-5 h-5 min-w-[1.25rem] rounded-full border flex items-center justify-center transition-colors ${selectedJob === job.id
                          ? "border-red-600 bg-red-600"
                          : "border-gray-300 bg-white group-hover:border-gray-400"
                          }`}>
                          {selectedJob === job.id && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                      </div>

                      {job.jobDescription && (
                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                          {job.jobDescription}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Test Configuration */}
            {selectedJob && (
              <div className="border-t border-gray-200 pt-8 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Settings className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        Configuration
                      </h2>
                      <p className="text-sm text-gray-500">Customize your assessment parameters</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Job Title input only for custom JD */}
                    {!allJobs.find((j) => j.id === selectedJob)?.isPreset && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Job Title (Custom)
                        </label>
                        <input
                          type="text"
                          value={customJobTitle}
                          onChange={(e) => setCustomJobTitle(e.target.value)}
                          placeholder="e.g., Senior React Developer Assessment"
                          className="block w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none shadow-sm"
                        />
                      </div>
                    )}

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Difficulty
                        </label>
                        <select
                          value={testConfig.difficulty}
                          onChange={(e) =>
                            setTestConfig({
                              ...testConfig,
                              difficulty: e.target.value,
                            })
                          }
                          className="block w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none shadow-sm cursor-pointer"
                        >
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                          <option value="mixed">Mixed</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Duration (min)
                        </label>
                        <input
                          type="number"
                          value={testConfig.duration}
                          onChange={(e) =>
                            setTestConfig({
                              ...testConfig,
                              duration: parseInt(e.target.value),
                            })
                          }
                          className="block w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none shadow-sm"
                          min="15"
                          max="180"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Questions
                        </label>
                        <input
                          type="number"
                          value={testConfig.questionCount}
                          onChange={(e) =>
                            setTestConfig({
                              ...testConfig,
                              questionCount: parseInt(e.target.value),
                            })
                          }
                          className="block w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none shadow-sm"
                          min="5"
                          max="50"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-6 pt-2">
                      <label className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors shadow-sm flex-1">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            checked={testConfig.includeScenarios}
                            onChange={(e) =>
                              setTestConfig({
                                ...testConfig,
                                includeScenarios: e.target.checked,
                              })
                            }
                            className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">Include scenario-based questions</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-gray-300 transition-colors shadow-sm flex-1">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            checked={testConfig.adaptiveTesting}
                            onChange={(e) =>
                              setTestConfig({
                                ...testConfig,
                                adaptiveTesting: e.target.checked,
                              })
                            }
                            className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">Enable adaptive testing</span>
                      </label>
                    </div>

                    <div className="pt-4">
                      <button
                        onClick={handleGenerateTest}
                        disabled={!selectedJob}
                        className="w-full flex items-center justify-center px-8 py-4 bg-gradient-to-r from-red-700 to-red-900 text-white text-lg font-semibold rounded-xl hover:from-red-800 hover:to-red-950 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                      >
                        <Brain className="w-6 h-6 mr-3" />
                        Generate Test
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Brain className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Intelligent Powered</h3>
          <p className="text-sm text-gray-600">
            Advanced Intelligence generates relevant questions based on job requirements
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Adaptive Testing</h3>
          <p className="text-sm text-gray-600">
            Questions adapt to candidate performance for accurate assessment
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Clock className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Time Efficient</h3>
          <p className="text-sm text-gray-600">
            Generate comprehensive tests in minutes, not hours
          </p>
        </div>
      </div>
    </div>
  );
};

export default TestGenerator;
